import React, { useCallback, useMemo } from 'react';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router-dom';
import styles from '@/pages/decedent-initiation/styles.less';
import logo from '@/common/assets/logo.svg';
import LogoutLink from '@/common/components/LogoutLink/index';
import Stepper from '@/common/components/Stepper/index';
import { useStepperContext, useToggle } from '@/common/hooks/index';
import LanguageDropdown from '@/common/components/NavigationBar/components/LanguageDropdown';
import { useTranslation } from 'react-i18next';
import {
  DecedentMainInfo,
  DecedentResidence,
  DecedentDependants,
  DecedentMarital,
  DecedentDocs,
} from '@/common/containers/Forms/index';
import { TEXT, STEPS, STEPS_FORM } from '@/pages/decedent-initiation/constants';
import { routes } from '@/app/routing/index';
import { TASK_MODULES } from '@/common/constants/constants';
import UserIntro from '@/common/containers/UserIntro/index';
import { MARKETING_SITE_URL } from '@/app/config';
import { Box } from '@mui/material';

const DecedentInitiation = ({
  decedent,
  editDecedent,
  submitQuestion,
  showIntro,
}) => {
  const { t } = useTranslation();
  const [intro, toggleInto] = useToggle(showIntro);
  const history = useHistory();
  const { nextStep, prevStep, currentStepInd } = useStepperContext();

  const onSubmit = useCallback(
    (form) => {
      if (!decedent?.id) {
        return Promise.reject(new Error('Missing decedent id'));
      }

      const getAction = () => {
        switch (currentStepInd) {
          case 0:
          case 1: {
            return editDecedent;
          }
          case 2:
          case 3:
          case 4: {
            return ({ id, form }) =>
              submitQuestion({
                decedentId: id,
                answers: form,
                section: STEPS_FORM[currentStepInd],
              });
          }
          default:
            return null;
        }
      };
      const action = getAction();
      if (!action) {
        return Promise.reject(new Error('Unsupported initiation step'));
      }

      action({ id: decedent.id, form }).then(() => {
        return currentStepInd === STEPS.length - 1
          ? history.push(
              `${routes.dashboard.taskList}/${TASK_MODULES.IMMEDIATE}`
            )
          : nextStep();
      });
    },
    [
      currentStepInd,
      decedent?.id,
      editDecedent,
      history,
      nextStep,
      submitQuestion,
    ]
  );

  const Form = useMemo(() => {
    const props = {
      formId: 'basicInfo',
      className: styles.form,
      onSubmit,
    };
    if ([0, 1].includes(currentStepInd)) props.initialValues = decedent;

    const forms = {
      0: DecedentMainInfo,
      1: DecedentResidence,
      2: DecedentMarital,
      3: DecedentDependants,
      4: DecedentDocs,
    };
    const Component = forms[currentStepInd];
    return Component ? React.cloneElement(<Component />, props) : null;
  }, [currentStepInd, onSubmit, decedent]);

  const text = useMemo(() => TEXT[currentStepInd], [currentStepInd]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <Box component="a" href={MARKETING_SITE_URL} display="inline-flex">
          <Box
            component="img"
            sx={{
              width: '205px',
              height: '40px',
              display: 'block',
              flexShrink: 0,
            }}
            src={logo}
            alt={t('Praedias logo')}
          />
        </Box>
        <div className={styles.headerActions}>
          <LogoutLink />
          <LanguageDropdown />
        </div>
      </header>
      {intro ? (
        <UserIntro className={styles.intro} onFinish={toggleInto} />
      ) : (
        <div className={styles.body}>
          <div className={styles.steps}>
            <Stepper />
          </div>
          <section className={styles.container}>
            <h4 className={styles.pageTitle}>{t(text.titleKey)}</h4>
            <p className={styles.pageLabel}>{t(text.labelKey)}</p>
            {Form}
          </section>
          <div className={styles.footer}>
            {currentStepInd > 0 && (
              <Button onClick={prevStep} variant="outlined" color="secondary">
                {t('Previous step')}
              </Button>
            )}
            <div className={styles.footerSubmit}>
              <Button type="submit" form="basicInfo" variant="contained">
                {t('Continue')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default DecedentInitiation;
